<script setup>
import { ref, computed } from "vue";
import { useGetters } from "vuex-composition-helpers";
import _ from "lodash";

import { FEEDBACK_CONFIGS } from "@/components/recommendationFeedbacks/lib/feedbackConfigs";
import FeedbackDropdownMenu from "@/components/recommendationFeedbacks/FeedbackDropdownMenu";

const props = defineProps({
  value: {
    type: String,
    default: null,
  },
  availableFeedbacks: {
    type: Array,
    default: undefined,
  },
  type: {
    type: String,
    required: true,
  },
  size: {
    type: String,
    default: "large",
  },
  tooltip: {
    type: String,
    default: null,
  },
  isInvertedColor: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: false,
  },
  onChange: {
    type: Function,
    required: true,
  },
  onDelete: {
    type: Function,
    required: false,
    default: null,
  },
});

const containerRef = ref(null);

defineExpose({
  containerRef,
});

const {
  currentTeam,
} = useGetters([
  "currentTeam",
]);

const availableFeedbackConfigs = computed(() => {
  let selectable = _.filter(FEEDBACK_CONFIGS, (fc) => fc.selectable);

  // Filter out soft criteria (positive) when best match is not enabled
  let filtered = selectable;
  if (!currentTeam.value?.bestMatchEnabled) {
    filtered = _.filter(filtered, (fc) => fc.feedback !== "positive");
  }

  return _.filter(filtered, (fc) => (!props.availableFeedbacks || _.includes(props.availableFeedbacks, fc?.feedback)) && fc?.feedback !== currentFeedbackConfig.value?.feedback);
});

const pillClasses = computed(() => {
  let classes = [
    "value-content-container",
    `value-content-container-${props.type}`,
    `value-content-container-${props.size}`,
    {
      "value-content-container-no-value": !props.value,
      "value-content-container-inverted": props.isInvertedColor,
    },
  ];
  if (currentFeedbackConfig.value) {
    classes.push(`value-content-container-${currentFeedbackConfig.value.feedback}`);
  }
  return classes;
});

const currentFeedbackConfig = computed(() => {
  return _.find(FEEDBACK_CONFIGS, { feedback: props.value })
});

const dropdownCaretIcon = ref("expand_more");
function onDropdownVisible(visible) {
  dropdownCaretIcon.value = visible ? "expand_less" : "expand_more";
}
</script>

<template>
  <div ref="containerRef" class="value-container">
    <el-dropdown trigger="click" @visible-change="onDropdownVisible">
      <el-tooltip
        placement="top"
        :content="tooltip"
        :disabled="!tooltip"
        :open-delay="100"
      >
        <!-- <img
          v-if="currentFeedbackConfig"
          :src="currentFeedbackConfig.src"
          class="value-content-image"
        /> -->

        <div :class="pillClasses">
          <span class="value-label">
            <slot />
          </span>
          <i class="material-icons-round md-18">{{ dropdownCaretIcon }}</i>
        </div>
      </el-tooltip>

      <el-dropdown-menu slot="dropdown">
        <FeedbackDropdownMenu
          :type="type"
          :current-feedback-type="currentFeedbackConfig?.feedback"
          :available-options="availableFeedbackConfigs.map(fc => fc.feedback)"
          :show-remove="!!onDelete"
          :remove-disabled="required"
          remove-tooltip="Filter is required. Please add a new one before removing this one."
          @option-click="onChange"
          @remove-click="onDelete"
        />
      </el-dropdown-menu>
    </el-dropdown>
  </div>
</template>

<style scoped lang="scss">
.value-container {
  display: inline-block;
  position: relative;
  flex-shrink: 0;

  .value-content-container {
    display: flex;
    cursor: pointer;
    padding: 3px 6px;
    align-items: center;
    border-width: 1px;
    border-style: solid;
    border-radius: $tf-global-border-radius;

    .value-content-image {
       height: 12px;
    }

    &.value-content-container-large {
      padding: 3px 6px;
      padding-right: 4px; // Smaller because of icon
    }

    &.value-content-container-small {
      padding: 0px 4px;
      padding-right: 2px; // Smaller because of icon

      .value-label {
        padding-right: 2px;
      }

      .value-content-image {
        margin-right: 2px;
        width: 10px;
        height: 10px;
      }
    }

    &:hover {
      background-color: hsla(30, 67%, 96%, 1);
    }

    .value-label {
      color: #2B3439;
      padding-right: 4px;
    }

    .value-content-image {
      margin-right: 4px;
    }

    &.value-content-container-positive_filter {
      background-color: $spring-green-10;
      border-color: $spring-green-5;
    }

    &.value-content-container-positive_recent_employments_filter {
      background-color: $spring-green-50;
      border-color: $spring-green-10;
    }

    &.value-content-container-similarity {
      background-color: $beige-30;
      border-color: $beige-10;
    }

    &.value-content-container-positive {
      background-color: $blue-10;
      border-color: $blue-5;
    }

    &.value-content-container-negative_filter {
      background-color: $magenta-30;
      border-color: $magenta-10;
    }

    &.value-content-container-no-value {
      &.value-content-container-inverted {
        background-color: white;
        border-color: #FAF1E6;
      }
    }
  }
}
</style>
